import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ApiError } from '@/shared/api/api-error';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { formatCurrency } from '@/shared/lib/format';

import {
  useAdjustProviderBalance,
  useCreateProvider,
  useDeleteProvider,
  useSetProviderStatus,
  useUpdateProviderBalanceSettings,
} from '../api/providers.queries';
import {
  balanceAdjustmentSchema,
  balanceSettingsSchema,
  createProviderSchema,
  type BalanceAdjustmentValues,
  type BalanceSettingsValues,
  type CreateProviderValues,
} from '../schemas/mapping.schema';
import type { Provider } from '../types/provider.types';

/** Map server field errors onto a form's fields. */
function useServerFieldErrors(error: unknown, setError: (field: string, message: string) => void) {
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      setError(field.charAt(0).toLowerCase() + field.slice(1), message);
    }
  }, [error, setError]);
}

/**
 * Activate or deactivate a provider. Deactivating needs a reason and says how
 * many items lose their only provider. Mount only while open.
 */
export function ProviderStatusDialog({
  provider,
  onClose,
}: {
  provider: Provider;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const setStatus = useSetProviderStatus();
  const deactivating = provider.isActive;
  const missingReason = deactivating && reason.trim() === '';
  const serverError =
    setStatus.error instanceof ApiError ? setStatus.error.fieldErrors?.Reason : undefined;

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={deactivating ? `Deactivate ${provider.name}?` : `Activate ${provider.name}?`}
      description={
        deactivating ? (
          <>
            No order will be sent to {provider.name}. <strong>{provider.soleProviderCount}</strong>{' '}
            active item
            {provider.soleProviderCount === 1 ? '' : 's'} rely on it alone and will stop being
            purchasable in every country.
          </>
        ) : (
          <>{provider.name} can supply its mapped items again.</>
        )
      }
      confirmLabel={deactivating ? 'Deactivate' : 'Activate'}
      variant={deactivating ? 'destructive' : 'default'}
      isPending={setStatus.isPending}
      onConfirm={() => {
        if (missingReason) return;
        const trimmed = reason.trim();
        setStatus.mutate(
          {
            providerId: provider.id,
            isActive: !deactivating,
            reason: trimmed === '' ? null : trimmed,
          },
          { onSuccess: onClose },
        );
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="provider-status-reason" className="text-xs">
          Reason{' '}
          <span className="text-muted-foreground font-normal">
            ({deactivating ? 'required' : 'optional'}, kept in the audit log)
          </span>
        </Label>
        <Input
          id="provider-status-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={500}
          aria-invalid={serverError ? true : undefined}
          className="h-8 text-xs"
        />
        {serverError && <p className="text-destructive text-xs">{serverError}</p>}
      </div>
    </ConfirmDialog>
  );
}

/**
 * Why a provider cannot be deleted, or null when nothing we know of uses it.
 * A pending balance adjustment is not in the provider row; the Admin API
 * refuses that case (and re-checks the rest under a lock).
 */
export function providerDeleteBlocker(provider: Provider): string | null {
  if (provider.mappingCount > 0) {
    return `Mapped to ${provider.mappingCount} denomination${provider.mappingCount === 1 ? '' : 's'}`;
  }
  if (provider.hasOrders) return 'Has orders';
  if (provider.balance !== 0) return 'Balance is not zero';
  return null;
}

/** Deletes a provider nothing uses. Mount only while open; `onDeleted` runs after success. */
export function ProviderDeleteDialog({
  provider,
  onClose,
  onDeleted,
}: {
  provider: Provider;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [reason, setReason] = useState('');
  const deleteProvider = useDeleteProvider();

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Delete ${provider.name}?`}
      description={
        <>
          Nothing uses <strong>{provider.name}</strong>: it has no mappings, no orders and a zero
          balance. It will be removed permanently, for every country. This can&apos;t be undone.
        </>
      }
      confirmLabel="Delete"
      variant="destructive"
      isPending={deleteProvider.isPending}
      onConfirm={() => {
        const trimmed = reason.trim();
        deleteProvider.mutate(
          { providerId: provider.id, name: provider.name, reason: trimmed === '' ? null : trimmed },
          {
            onSuccess: () => {
              onClose();
              onDeleted();
            },
            // Closed on a refusal too: the toast explains it and the counts refresh.
            onError: onClose,
          },
        );
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="provider-delete-reason" className="text-xs">
          Reason{' '}
          <span className="text-muted-foreground font-normal">
            (optional, kept in the audit log)
          </span>
        </Label>
        <Input
          id="provider-delete-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={500}
          disabled={deleteProvider.isPending}
          className="h-8 text-xs"
        />
      </div>
    </ConfirmDialog>
  );
}

/**
 * Adds a provider. It starts inactive with a zero balance: top it up with a
 * balance adjustment and activate it once its integration is live. Mount only
 * while open; `onCreated` receives the new provider's id.
 */
export function ProviderCreateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (providerId: number) => void;
}) {
  const create = useCreateProvider();
  const form = useForm<CreateProviderValues>({
    resolver: zodResolver(createProviderSchema),
    defaultValues: {
      name: '',
      currencyCode: 'USD',
      isBalanceConsidered: true,
      lowBalanceThreshold: Number.NaN,
    },
  });
  useServerFieldErrors(create.error, (field, message) => {
    if (field === 'name' || field === 'currencyCode') {
      form.setError(field, { type: 'server', message });
    }
  });

  const onSubmit = (values: CreateProviderValues) =>
    create.mutate(
      {
        name: values.name.trim(),
        currencyCode: values.currencyCode.toUpperCase(),
        isBalanceConsidered: values.isBalanceConsidered,
        lowBalanceThreshold: Number.isNaN(values.lowBalanceThreshold)
          ? null
          : values.lowBalanceThreshold,
      },
      {
        onSuccess: (providerId) => {
          onClose();
          onCreated(providerId);
        },
      },
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">New provider</DialogTitle>
          <DialogDescription className="text-xs">
            The provider starts inactive with a zero balance. Orders reach it only after its
            integration is live and you activate it.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={100} autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currencyCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      onChange={(event) => field.onChange(event.target.value.toUpperCase())}
                      maxLength={3}
                      className="w-24 uppercase"
                    />
                  </FormControl>
                  <FormDescription>
                    What the provider charges in. Costs and the balance use it; it can&apos;t be
                    changed later.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isBalanceConsidered"
              render={({ field }) => (
                <FormItem className="border-border flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Only use when the balance covers the cost</FormLabel>
                    <FormDescription>Turn off for credit or post-paid providers.</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lowBalanceThreshold"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Low-balance alarm</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={Number.isNaN(field.value) ? '' : field.value}
                      onChange={(event) => field.onChange(event.target.valueAsNumber)}
                      type="number"
                      min={0}
                      step="0.01"
                    />
                  </FormControl>
                  <FormDescription>Optional. Warning only; it never stops sales.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={create.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                Create provider
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

/** Balance enforcement and the low-balance alarm. Mount only while open. */
export function ProviderBalanceSettingsDialog({
  provider,
  onClose,
}: {
  provider: Provider;
  onClose: () => void;
}) {
  const update = useUpdateProviderBalanceSettings();
  const form = useForm<BalanceSettingsValues>({
    resolver: zodResolver(balanceSettingsSchema),
    defaultValues: {
      isBalanceConsidered: provider.isBalanceConsidered,
      lowBalanceThreshold: provider.lowBalanceThreshold ?? Number.NaN,
    },
  });

  const onSubmit = (values: BalanceSettingsValues) =>
    update.mutate(
      {
        providerId: provider.id,
        isBalanceConsidered: values.isBalanceConsidered,
        lowBalanceThreshold: Number.isNaN(values.lowBalanceThreshold)
          ? null
          : values.lowBalanceThreshold,
      },
      { onSuccess: onClose },
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">Balance settings — {provider.name}</DialogTitle>
          <DialogDescription className="text-xs">
            Amounts are in {provider.currencyCode ?? 'the provider’s currency'}.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="isBalanceConsidered"
              render={({ field }) => (
                <FormItem className="border-border flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Only use when the balance covers the cost</FormLabel>
                    <FormDescription>
                      {field.value
                        ? `On: checkout skips ${provider.name} for any voucher that costs more than its recorded balance and uses the next cheapest provider. If none is left, the voucher can’t be sold.`
                        : `Off: checkout ignores ${provider.name}’s balance and can pick it even at zero. Use this for credit or post-paid providers.`}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lowBalanceThreshold"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Low-balance alarm</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={Number.isNaN(field.value) ? '' : field.value}
                      onChange={(event) => field.onChange(event.target.valueAsNumber)}
                      type="number"
                      min={0}
                      step="0.01"
                    />
                  </FormControl>
                  <FormDescription>
                    Warning only — marks {provider.name} as low at or below this balance; it never
                    stops sales. Leave empty for no alarm.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={update.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
                {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                Save
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

/** Top up (positive) or correct (negative) the prepaid balance. Mount only while open. */
export function ProviderBalanceAdjustmentDialog({
  provider,
  onClose,
}: {
  provider: Provider;
  onClose: () => void;
}) {
  const adjust = useAdjustProviderBalance();
  const form = useForm<BalanceAdjustmentValues>({
    resolver: zodResolver(balanceAdjustmentSchema),
    defaultValues: { amount: Number.NaN, reason: '' },
  });
  useServerFieldErrors(adjust.error, (field, message) => {
    if (field === 'amount' || field === 'reason') form.setError(field, { type: 'server', message });
  });

  const amount = form.watch('amount');
  const currency = provider.currencyCode ?? 'USD';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">Adjust balance — {provider.name}</DialogTitle>
          <DialogDescription className="text-xs">
            Current balance {formatCurrency(provider.balance, currency)}. Use a negative amount to
            deduct. Large adjustments are sent for approval first.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) =>
              adjust.mutate(
                { providerId: provider.id, amount: values.amount, reason: values.reason.trim() },
                { onSuccess: onClose },
              ),
            )}
            className="space-y-5"
          >
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount ({currency})</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={Number.isNaN(field.value) ? '' : field.value}
                      onChange={(event) => field.onChange(event.target.valueAsNumber)}
                      type="number"
                      step="0.01"
                      autoFocus
                    />
                  </FormControl>
                  {!Number.isNaN(amount) && amount !== 0 && (
                    <FormDescription>
                      New balance {formatCurrency(provider.balance + amount, currency)}.
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Transfer reference, reconciliation note…" />
                  </FormControl>
                  <FormDescription>Kept in the audit log.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={adjust.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={adjust.isPending}>
                {adjust.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                Adjust balance
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
